import * as React from "react";

import Box from "@mui/material/Box";
import Modal from "@mui/material/Modal";
import Stack from "@mui/material/Stack";
import Button from "@mui/material/Button";
import Tooltip from "@mui/material/Tooltip";

import TuneRoundedIcon from "@mui/icons-material/TuneRounded";
import RestartAltRoundedIcon from "@mui/icons-material/RestartAltRounded";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";

import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import moment from "moment";

import DeviceDropDown from "./StatusDropdown.tsx";

import { toast } from "../../components/Toast/toast.ts";

import "../../css/FilterModal.css";

import { useUserContext } from "../../../src/context/UserContext.tsx";

const style = {
  position: "absolute" as const,

  top: "50%",
  left: "50%",

  transform: "translate(-50%, -50%)",

  width: {
    xs: "92%",
    sm: 440,
  },

  borderRadius: "8px",

  background: "rgb(var(--surface-1))",

  border: "1px solid rgb(var(--border-primary))",

  boxShadow: "var(--shadow-card-hover)",

  overflow: "hidden",

  outline: "none",

  backdropFilter: "blur(18px)",
};

export default function TriggerFilterModal({
  setFilterVal,
  setLoading,
  setIsAllDataFetched,
  status,
  setStatus,
  todate,
  setTodate,
  fromdate,
  setFromdate,
  setCurrentCount,
  fetchCountAndData,
  setCurrentFilterCount,
  fetchFilteredData,
}: any) {
  const [open, setOpen] = React.useState(false);

  useUserContext();

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleFromDateChange = (newValue: any) => {
    setFromdate(newValue);

    if (newValue && todate && moment(newValue).isAfter(todate)) {
      setTodate(null);
    }
  };

  const handleToDateChange = (newValue: any) => {
    setTodate(newValue);

    if (newValue && fromdate && moment(fromdate).isAfter(newValue)) {
      setFromdate(null);
    }
  };

  const ResetFilter = async () => {
    try {
      setFilterVal(false);

      setStatus("");

      setFromdate(null);

      setTodate(null);

      fetchCountAndData();

      setIsAllDataFetched(false);

      setCurrentCount(20);

      setCurrentFilterCount(20);
    } catch (error) {
      toast.error("Something went wrong!");
    }
  };

  return (
    <>
      {/* Trigger */}

      <Tooltip title="Filter Reports" arrow>
        <button
          onClick={handleOpen}
          className="
            flex
            items-center
            justify-center
            w-9
            h-9
            rounded-md
            transition-all
            duration-300
            border
            hover:scale-[1.03]
            active:scale-[0.98]
          "
          style={{
            background: "rgb(var(--surface-2))",

            borderColor: "rgb(var(--border-primary))",

            color: "rgb(var(--text-secondary))",

            boxShadow: "var(--shadow-sm)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor =
              "rgba(var(--color-gold-1),0.35)";

            e.currentTarget.style.background =
              "rgba(var(--color-gold-1),0.08)";

            e.currentTarget.style.color =
              "rgb(var(--color-gold-1))";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor =
              "rgb(var(--border-primary))";

            e.currentTarget.style.background =
              "rgb(var(--surface-2))";

            e.currentTarget.style.color =
              "rgb(var(--text-secondary))";
          }}
        >
          <TuneRoundedIcon fontSize="small" />
        </button>
      </Tooltip>

      {/* Modal */}

      <Modal
        open={open}
        onClose={handleClose}
      >
        <Box sx={style}>
          {/* Header */}

          <div
            className="
              flex
              items-center
              justify-between
              px-6
              py-5
            "
            style={{
              background:
                "linear-gradient(180deg, rgba(var(--color-gold-1),0.08) 0%, transparent 100%)",

              borderBottom:
                "1px solid rgb(var(--border-primary))",
            }}
          >
            <div className="flex flex-col">
              <span
                className="
                  text-[18px]
                  font-semibold
                  tracking-tight
                "
                style={{
                  color: "rgb(var(--text-primary))",
                }}
              >
                Advanced Filters
              </span>

              <span
                className="text-[12px] mt-1"
                style={{
                  color: "rgb(var(--text-muted))",
                }}
              >
                Filter reports by date and alert status
              </span>
            </div>

            <div
              className="
                flex
                items-center
                justify-center
                w-11
                h-11
                rounded-2xl
              "
              style={{
                background:
                  "rgba(var(--color-gold-1),0.12)",

                border:
                  "1px solid rgba(var(--color-gold-1),0.18)",
              }}
            >
              <FilterAltRoundedIcon
                sx={{
                  color: "rgb(var(--color-gold-1))",
                  fontSize: 20,
                }}
              />
            </div>
          </div>

          {/* Body */}

          <div className="p-6">
            <LocalizationProvider dateAdapter={AdapterMoment}>
              {/* Dates */}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {/* From */}

                <div className="flex flex-col gap-2">
                  <span
                    className="
                      text-[12px]
                      font-medium
                      uppercase
                      tracking-[0.08em]
                    "
                    style={{
                      color: "rgb(var(--text-muted))",
                    }}
                  >
                    From Date
                  </span>

                  <DatePicker
                    disableFuture
                    value={fromdate}
                    onChange={handleFromDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "52px",

                        borderRadius: "6px",

                        background:
                          "rgb(var(--surface-2))",

                        color:
                          "rgb(var(--text-primary))",

                        transition: "all 0.25s ease",
                      },

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgb(var(--border-primary))",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgba(var(--color-gold-1),0.35)",
                      },

                      "& .Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.45)",
                        },

                      "& .MuiInputLabel-root": {
                        color:
                          "rgb(var(--text-muted))",
                      },

                      "& .MuiInputBase-input": {
                        color:
                          "rgb(var(--text-primary))",

                        fontSize: "14px",
                      },

                      "& .MuiSvgIcon-root": {
                        color:
                          "rgb(var(--text-secondary))",
                      },
                    }}
                  />
                </div>

                {/* To */}

                <div className="flex flex-col gap-2">
                  <span
                    className="
                      text-[12px]
                      font-medium
                      uppercase
                      tracking-[0.08em]
                    "
                    style={{
                      color: "rgb(var(--text-muted))",
                    }}
                  >
                    To Date
                  </span>

                  <DatePicker
                    disableFuture
                    value={todate}
                    disabled={!fromdate}
                    minDate={fromdate}
                    onChange={handleToDateChange}
                    slotProps={{
                      textField: {
                        fullWidth: true,
                      },
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        height: "52px",

                        borderRadius: "6px",

                        background:
                          "rgb(var(--surface-2))",

                        color:
                          "rgb(var(--text-primary))",

                        transition: "all 0.25s ease",
                      },

                      "& .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgb(var(--border-primary))",
                      },

                      "&:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor:
                          "rgba(var(--color-gold-1),0.35)",
                      },

                      "& .Mui-focused .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor:
                            "rgba(var(--color-gold-1),0.45)",
                        },

                      "& .MuiInputLabel-root": {
                        color:
                          "rgb(var(--text-muted))",
                      },

                      "& .MuiInputBase-input": {
                        color:
                          "rgb(var(--text-primary))",

                        fontSize: "14px",
                      },

                      "& .MuiSvgIcon-root": {
                        color:
                          "rgb(var(--text-secondary))",
                      },
                    }}
                  />
                </div>
              </div>

              {/* Status */}

              <div className="mt-5">
                <DeviceDropDown
                  status={status}
                  setStatus={setStatus}
                />
              </div>

              {/* Active Filters */}

              {(fromdate || todate || status) && (
                <div
                  className="
                    mt-5
                    rounded-2xl
                    px-4
                    py-4
                  "
                  style={{
                    background:
                      "rgba(var(--color-gold-1),0.08)",

                    border:
                      "1px solid rgba(var(--color-gold-1),0.16)",
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex flex-col">
                      <span
                        className="
                          text-[11px]
                          uppercase
                          tracking-[0.08em]
                        "
                        style={{
                          color:
                            "rgb(var(--text-muted))",
                        }}
                      >
                        Active Filters
                      </span>

                      <span
                        className="text-[13px] mt-1 font-medium"
                        style={{
                          color:
                            "rgb(var(--text-primary))",
                        }}
                      >
                        {fromdate
                          ? moment(fromdate).format(
                              "DD MMM YYYY"
                            )
                          : "--"}{" "}
                        →{" "}
                        {todate
                          ? moment(todate).format(
                              "DD MMM YYYY"
                            )
                          : "--"}
                      </span>
                    </div>

                    {status && (
                      <span
                        className="
                          text-[11px]
                          font-semibold
                          px-3
                          py-1
                          rounded-full
                        "
                        style={{
                          background:
                            "rgba(var(--color-gold-1),0.12)",

                          color:
                            "rgb(var(--color-gold-1))",

                          border:
                            "1px solid rgba(var(--color-gold-1),0.18)",
                        }}
                      >
                        {status}
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Footer Buttons */}

              <Stack
                direction="row"
                spacing={1.5}
                sx={{
                  marginTop: "28px",
                  justifyContent: "flex-end",
                }}
              >
                <Button
                  variant="contained"
                  startIcon={<RestartAltRoundedIcon />}
                  onClick={() => {
                    ResetFilter();
                    handleClose();
                  }}
                  sx={{
                    height: "36px",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    paddingInline: "18px",
                    background:
                      "rgb(var(--surface-2))",
                    color:
                      "rgb(var(--text-primary))",
                    border:
                      "1px solid rgb(var(--border-primary))",
                    boxShadow: "none",
                    "&:hover": {
                      background:
                        "rgba(239,68,68,0.08)",
                      borderColor:
                        "rgba(239,68,68,0.25)",
                      color: "#ef4444",
                      boxShadow: "none",
                    },
                  }}
                >
                  Reset
                </Button>

                <Button
                  variant="contained"
                  startIcon={<CheckRoundedIcon />}
                  onClick={() => {
                    fetchFilteredData();

                    handleClose();
                  }}
                  disabled={
                    !todate &&
                    !fromdate &&
                    !status
                  }
                  sx={{
                    height: "36px",
                    borderRadius: "6px",
                    textTransform: "none",
                    fontWeight: 600,
                    paddingInline: "22px",
                    background:
                      "var(--gold-grad)",
                    color: "#fff",
                    boxShadow: "none",
                    "&:hover": {
                      background:
                        "rgb(var(--color-gold-2))",

                      boxShadow:
                        "0 10px 30px rgba(var(--color-gold-1),0.25)",
                    },
                  }}
                >
                  Apply Filters
                </Button>
              </Stack>
            </LocalizationProvider>
          </div>
        </Box>
      </Modal>
    </>
  );
}